import { useState, useEffect, useRef } from 'react';
import { Table, Tag, Space, Button, Popconfirm, message } from 'antd';
import { EditOutlined, DeleteOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import billService from '../../services/bill';

const BillList = ({ filters, onEdit, refreshKey, onSelectionChange }) => {
  const [bills, setBills] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0
  });

  const onSelectionChangeRef = useRef(onSelectionChange);
  onSelectionChangeRef.current = onSelectionChange;

  // 获取账单数据
  const fetchBills = async (page, pageSize) => {
    setLoading(true);
    try {
      const result = await billService.getBills({ page, pageSize, ...filters });
      setBills(result.bills);
      setPagination(prev => ({
        ...prev,
        current: result.page,
        pageSize: pageSize,
        total: result.total
      }));
      setSelectedRowKeys([]);
      if (onSelectionChangeRef.current) {
        onSelectionChangeRef.current([]);
      }
    } catch (error) {
      message.error('获取账单列表失败');
    } finally {
      setLoading(false);
    }
  };

  // 筛选条件或刷新key变化时重新加载
  useEffect(() => {
    fetchBills(1, pagination.pageSize);
  }, [filters, refreshKey]);

  // 删除单条记录
  const handleDelete = async (id) => {
    try {
      await billService.deleteBill(id);
      message.success('删除成功');
      fetchBills(pagination.current, pagination.pageSize);
    } catch (error) {
      message.error('删除失败');
    }
  };

  // 选择变化
  const handleSelectionChange = (keys) => {
    setSelectedRowKeys(keys);
    if (onSelectionChangeRef.current) {
      onSelectionChangeRef.current(keys);
    }
  };

  // 分页变化
  const handleTableChange = (pag) => {
    fetchBills(pag.current, pag.pageSize);
  };

  const columns = [
    {
      title: '交易时间',
      dataIndex: 'transactionTime',
      key: 'transactionTime',
      width: 140,
      render: (time) => dayjs(time).format('YYYY-MM-DD HH:mm')
    },
    {
      title: '收/支',
      dataIndex: 'transactionType',
      key: 'transactionType',
      width: 60,
      render: (type) => (
        <Tag color={type === 'income' ? 'green' : 'red'}>
          {type === 'income' ? '收入' : '支出'}
        </Tag>
      )
    },
    {
      title: '来源',
      dataIndex: 'sourcePlatform',
      key: 'sourcePlatform',
      width: 70,
      render: (platform) => {
        const map = {
          wechat: { text: '微信', color: '#07c160' },
          alipay: { text: '支付宝', color: '#1677ff' },
          manual: { text: '手动', color: '#8c8c8c' }
        };
        const p = map[platform] || { text: platform, color: '#8c8c8c' };
        return <Tag color={p.color}>{p.text}</Tag>;
      }
    },
    {
      title: '交易对方',
      dataIndex: 'counterparty',
      key: 'counterparty',
      ellipsis: true,
      width: 120
    },
    {
      title: '商品说明',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
      width: 140
    },
    {
      title: '金额',
      dataIndex: 'amount',
      key: 'amount',
      width: 90,
      align: 'right',
      render: (amount, record) => {
        const color = record.transactionType === 'income' ? '#52c41a' : '#f5222d';
        const prefix = record.transactionType === 'income' ? '+' : '-';
        return (
          <span style={{ color, fontWeight: 500 }}>
            {prefix}¥{amount.toFixed(2)}
          </span>
        );
      }
    },
    {
      title: '支付方式',
      dataIndex: 'paymentMethod',
      key: 'paymentMethod',
      width: 90,
      ellipsis: true,
      render: (method) => method || '-'
    },
    {
      title: '交易状态',
      dataIndex: 'status',
      key: 'status',
      width: 80,
      render: (status) => {
        if (!status) return '-';
        const statusMap = {
          '交易成功': { color: 'green', text: '成功' },
          '已退款': { color: 'orange', text: '已退款' },
          '退款成功': { color: 'orange', text: '已退款' },
          '支付成功': { color: 'green', text: '成功' },
          '已关闭': { color: 'default', text: '已关闭' },
          '等待付款': { color: 'blue', text: '待付款' }
        };
        const s = statusMap[status] || { color: 'default', text: status };
        return <Tag color={s.color}>{s.text}</Tag>;
      }
    },
    {
      title: '分类',
      dataIndex: 'originalType',
      key: 'originalType',
      width: 100,
      ellipsis: true,
      render: (type) => type || '-'
    },
    {
      title: '操作',
      key: 'action',
      width: 80,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Button 
            type="link" 
            size="small" 
            icon={<EditOutlined />} 
            onClick={() => onEdit(record)} 
          />
          <Popconfirm 
            title="确定删除此账单？" 
            onConfirm={() => handleDelete(record.id)} 
            okText="确定" 
            cancelText="取消"
          >
            <Button type="link" size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <Table
      rowSelection={{
        selectedRowKeys,
        onChange: handleSelectionChange
      }}
      columns={columns}
      dataSource={bills}
      rowKey="id"
      loading={loading}
      pagination={{
        current: pagination.current,
        pageSize: pagination.pageSize,
        total: pagination.total,
        showSizeChanger: true,
        showQuickJumper: true,
        showTotal: (total) => `共 ${total} 条`,
        pageSizeOptions: ['10', '20', '50', '100']
      }}
      onChange={handleTableChange}
      size="middle"
      scroll={{ x: 1100, y: 'calc(100vh - 380px)' }}
    />
  );
};

export default BillList;
